import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  Modal,
  RefreshControl,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { API_URL } from '../config';
import { styles as mobileStyles } from '../styles/pages/prometheusMonitoringStyles';
import { styles as desktopStyles } from '../styles/pages/prometheusMonitoringDesktopStyles';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface MetricData {
  timestamp: string;
  value: number;
}

interface ServerMetrics {
  id: number;
  name: string;
  server: string;
  port: number;
  cpuUsage: MetricData[];
  memoryUsage: MetricData[];
  diskUsage: MetricData[];
  networkIn: MetricData[];
  networkOut: MetricData[];
  loadAverage: MetricData[];
  lastUpdated: Date;
  status: 'online' | 'offline' | 'error';
}

const PrometheusMonitoring: React.FC = () => {
  const [servers, setServers] = useState<ServerMetrics[]>([]);
  const [newServerName, setNewServerName] = useState('');
  const [newServer, setNewServer] = useState('');
  const [newPort, setNewPort] = useState('9090');
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [maxDataPoints] = useState(60); // 최대 60개 데이터 포인트 (30초 간격으로 30분)
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState<string>('');
  
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const styles = isDesktop ? { ...mobileStyles, ...desktopStyles } : mobileStyles;

  useEffect(() => {
    fetchServers();
    startAutoRefresh();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refreshInterval]);

  const startAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      fetchServers();
    }, refreshInterval * 1000);
  };

  const fetchServers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/prometheus/servers`);
      const result = await response.json();
      setServers(result.data || []);
    } catch (error) {
      console.error('Failed to fetch servers:', error);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchServers();
    setRefreshing(false);
  };

  const addServer = async () => {
    if (!newServerName || !newServer || !newPort) {
      Alert.alert('오류', '서버 이름, IP와 포트를 모두 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/prometheus/servers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name: newServerName,
          server: newServer,
          port: parseInt(newPort) 
        }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setNewServerName('');
        setNewServer('');
        setNewPort('9090');
        setModalVisible(false);
        fetchServers();
      } else {
        Alert.alert('오류', result.error || '서버 추가에 실패했습니다.');
      }
    } catch (error) {
      Alert.alert('오류', '네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number, name: string) => {
    setDeleteTargetId(id);
    setDeleteTargetName(name);
    setDeleteConfirmVisible(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/prometheus/servers/${deleteTargetId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Delete response:', result);
        await fetchServers();
      } else {
        const errorResult = await response.json();
        Alert.alert('오류', errorResult.message || errorResult.error || '서버 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      Alert.alert('오류', '서버 삭제에 실패했습니다.');
    } finally {
      setDeleteConfirmVisible(false);
      setDeleteTargetId(null);
      setDeleteTargetName('');
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmVisible(false);
    setDeleteTargetId(null);
    setDeleteTargetName('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return '#4CAF50';
      case 'offline':
        return '#F44336';
      case 'error':
        return '#FF9800';
      default:
        return '#757575';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online':
        return '온라인';
      case 'offline':
        return '오프라인';
      case 'error':
        return '오류';
      default:
        return '알 수 없음';
    }
  };

  const createChartData = (data: MetricData[], label: string, color: string): ChartData<'line'> => {
    // 최근 30분의 데이터만 필터링
    const now = Date.now();
    const thirtyMinutesAgo = now - 30 * 60 * 1000;
    
    const filteredData = data.filter(d => {
      const timestamp = new Date(d.timestamp).getTime();
      return timestamp >= thirtyMinutesAgo && timestamp <= now;
    });
    
    // 시간순으로 정렬 (중요!)
    const sortedData = filteredData.sort((a, b) => {
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });
    
    // Grafana 스타일: 실제 데이터 포인트만 사용
    const chartData = sortedData.map(d => ({
      x: new Date(d.timestamp).getTime(),
      y: d.value
    }));
    
    return {
      datasets: [
        {
          label,
          data: chartData,
          borderColor: color,
          backgroundColor: `${color}33`,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4,
          fill: false,
        },
      ],
    };
  };

  const getChartOptions = (isPercentage: boolean = true): ChartOptions<'line'> => {
    const now = Date.now();
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        },
      },
      scales: {
        x: {
          type: 'time' as const,
          time: {
            unit: 'minute',
            displayFormats: {
              minute: 'HH:mm',
              hour: 'HH:mm',
            },
            tooltipFormat: 'yyyy-MM-dd HH:mm:ss',
          },
          min: now - 30 * 60 * 1000,
          max: now,
          ticks: {
            source: 'auto',
            autoSkip: true,
            maxTicksLimit: 7,
            maxRotation: 45,
            minRotation: 45,
            font: {
              size: 10,
            },
          },
          grid: {
            display: true,
            color: 'rgba(200, 200, 200, 0.2)',
          },
        },
        y: {
          beginAtZero: true,
          ...(isPercentage && { max: 100 }),
          ticks: {
            font: {
              size: 10,
            },
          },
          grid: {
            display: true,
            color: 'rgba(200, 200, 200, 0.2)',
          },
        },
      },
    };
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}></Text>
        <View style={styles.headerControls}>
          <View style={styles.intervalControl}>
            <Text style={styles.intervalLabel}>갱신 간격:</Text>
            <TextInput
              style={styles.intervalInput}
              value={refreshInterval.toString()}
              onChangeText={(text) => {
                const value = parseInt(text) || 30;
                if (value >= 1 && value <= 300) {
                  setRefreshInterval(value);
                }
              }}
              keyboardType="numeric"
              placeholder="30"
            />
            <Text style={styles.intervalUnit}>초</Text>
          </View>
          <TouchableOpacity
            style={styles.addButton}
            onPress={() => setModalVisible(true)}
          >
            <Text style={styles.addButtonText}>+ 서버 추가</Text>
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {servers.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>등록된 서버가 없습니다.</Text>
            <Text style={styles.emptyText}>서버를 추가해주세요.</Text>
          </View>
        ) : (
          <View style={styles.gridContainer}>
            {servers.map((server) => (
              <View key={server.id} style={styles.serverCardWrapper}>
                <View style={styles.serverCard}>
                  <View style={styles.serverHeader}>
                    <Text style={styles.serverName} numberOfLines={1} ellipsizeMode="tail">
                      {server.name} ({server.server}:{server.port})
                    </Text>
                    <View
                      style={[
                        styles.statusBadge,
                        { backgroundColor: getStatusColor(server.status) },
                      ]}
                    >
                      <Text style={styles.statusText}>
                        {getStatusText(server.status)}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.metricsContainer}>
                    <View style={styles.metricRow}>
                      <View style={styles.metricChart}>
                        <View style={styles.metricHeader}>
                          <Text style={styles.metricLabel}>CPU 사용률 (%)</Text>
                          <Text style={styles.metricValue}>
                            {server.cpuUsage.length > 0
                              ? `${server.cpuUsage[0].value.toFixed(1)}%`
                              : '-'}
                          </Text>
                        </View>
                        <View style={styles.chartContainer}>
                          <Line 
                            data={createChartData(server.cpuUsage, 'CPU', '#2196F3')} 
                            options={getChartOptions(true)} 
                          />
                        </View>
                      </View>
                      <View style={styles.metricChart}>
                        <View style={styles.metricHeader}>
                          <Text style={styles.metricLabel}>메모리 사용률 (%)</Text>
                          <Text style={styles.metricValue}>
                            {server.memoryUsage.length > 0
                              ? `${server.memoryUsage[0].value.toFixed(1)}%`
                              : '-'}
                          </Text>
                        </View>
                        <View style={styles.chartContainer}>
                          <Line 
                            data={createChartData(server.memoryUsage, 'Memory', '#4CAF50')} 
                            options={getChartOptions(true)} 
                          />
                        </View>
                      </View>
                    </View>

                    <View style={styles.metricRow}>
                      <View style={styles.metricChart}>
                        <View style={styles.metricHeader}>
                          <Text style={styles.metricLabel}>디스크 사용률 (%)</Text>
                          <Text style={styles.metricValue}>
                            {server.diskUsage.length > 0
                              ? `${server.diskUsage[0].value.toFixed(1)}%`
                              : '-'}
                          </Text>
                        </View>
                        <View style={styles.chartContainer}>
                          <Line 
                            data={createChartData(server.diskUsage, 'Disk', '#FF9800')} 
                            options={getChartOptions(true)} 
                          />
                        </View>
                      </View>
                      <View style={styles.metricChart}>
                        <View style={styles.metricHeader}>
                          <Text style={styles.metricLabel}>로드 평균</Text>
                          <Text style={styles.metricValue}>
                            {server.loadAverage.length > 0
                              ? server.loadAverage[0].value.toFixed(2)
                              : '-'}
                          </Text>
                        </View>
                        <View style={styles.chartContainer}>
                          <Line 
                            data={createChartData(server.loadAverage, 'Load', '#9C27B0')} 
                            options={getChartOptions(false)} 
                          />
                        </View>
                      </View>
                    </View>

                    <View style={styles.metricRow}>
                      <View style={styles.metricChart}>
                        <View style={styles.metricHeader}>
                          <Text style={styles.metricLabel}>네트워크 In (KB/s)</Text>
                          <Text style={styles.metricValue}>
                            {server.networkIn.length > 0
                              ? `${(server.networkIn[0].value / 1024).toFixed(1)} KB/s`
                              : '-'}
                          </Text>
                        </View>
                        <View style={styles.chartContainer}>
                          <Line 
                            data={createChartData(server.networkIn, 'Network In', '#00BCD4')} 
                            options={getChartOptions(false)} 
                          />
                        </View>
                      </View>
                      <View style={styles.metricChart}>
                        <View style={styles.metricHeader}>
                          <Text style={styles.metricLabel}>네트워크 Out (KB/s)</Text>
                          <Text style={styles.metricValue}>
                            {server.networkOut.length > 0
                              ? `${(server.networkOut[0].value / 1024).toFixed(1)} KB/s`
                              : '-'}
                          </Text>
                        </View>
                        <View style={styles.chartContainer}>
                          <Line 
                            data={createChartData(server.networkOut, 'Network Out', '#FFC107')} 
                            options={getChartOptions(false)} 
                          />
                        </View>
                      </View>
                    </View>
                  </View>

                  <View style={styles.serverFooter}>
                    <Text style={styles.lastUpdated}>
                      마지막 업데이트: {new Date(server.lastUpdated).toLocaleString('ko-KR')}
                    </Text>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.deleteButton]}
                      onPress={() => handleDeleteClick(server.id, server.name)}
                    >
                      <Text style={styles.actionButtonText}>삭제</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modal}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>프로메테우스 서버 추가</Text>
            <TextInput
              style={styles.input}
              placeholder="서버 이름 (예: 개발 서버)"
              value={newServerName}
              onChangeText={setNewServerName}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <TextInput
              style={styles.input}
              placeholder="서버 IP (예: 192.168.1.100)"
              value={newServer}
              onChangeText={setNewServer}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <TextInput
              style={styles.input}
              placeholder="포트 (기본: 9090)"
              value={newPort}
              onChangeText={setNewPort}
              keyboardType="numeric"
              autoCapitalize="none"
              autoCorrect={false}
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setModalVisible(false)}
              >
                <Text style={styles.modalButtonText}>취소</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.confirmButton]}
                onPress={addServer}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text style={styles.modalButtonText}>추가</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={deleteConfirmVisible}
        onRequestClose={cancelDelete}
      >
        <View style={styles.modal}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>서버 삭제</Text>
            <Text style={styles.confirmText}>
              "{deleteTargetName}" 서버를 삭제하시겠습니까?
            </Text>
            <Text style={styles.warningText}>
              이 작업은 되돌릴 수 없습니다.
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={cancelDelete}
              >
                <Text style={styles.modalButtonText}>취소</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.deleteConfirmButton]}
                onPress={confirmDelete}
              >
                <Text style={styles.modalButtonText}>삭제</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default PrometheusMonitoring;